import { useState } from 'react';
import { CheckCircle2 } from 'lucide-react';
import { cn } from '../shared/utils/cn';
import { FileUpload } from '../features/files/components/FileUpload';
import apiClient from '../shared/lib/api-client';
import { personaService, ProcessingStatus } from '../shared/services/personaService';
interface UploadedFile {
  file: File;
  status: 'uploading' | 'success' | 'error';
  message: string;
}

export function WorkshopSubmissionPage() {
  const [uploadedFile, setUploadedFile] = useState<UploadedFile | null>(null);
  const [tableNumber, setTableNumber] = useState('');
  const [facilitatorName, setFacilitatorName] = useState('');
  const [segment, setSegment] = useState<'GDI' | 'TPC' | 'PFA' | ''>('');
  const [teamMembers, setTeamMembers] = useState('');
  const [highlights, setHighlights] = useState('');
  const [challenges, setChallenges] = useState('');
  const [usefulness, setUsefulness] = useState<number | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submissionMessage, setSubmissionMessage] = useState<string | null>(null);

  const isFormIncomplete =
    !uploadedFile ||
    !tableNumber.trim() ||
    !facilitatorName.trim() ||
    !segment ||
    !teamMembers.trim() ||
    !highlights.trim() ||
    !challenges.trim() ||
    usefulness === null;

  // Background polling (doesn't block UI)
  const pollStatusInBackground = async (fileId: string) => {
    let attempts = 0;
    const maxAttempts = 30;

    const poll = async () => {
      if (attempts >= maxAttempts) return;
      
      try {
        const statusResponse = await personaService.getProcessingStatus(fileId);

        if (statusResponse.status === 'completed') {
          console.log('✅ Processing completed:', statusResponse.message);
          return; // Done
        } else if (statusResponse.status === 'failed') {
          console.error('❌ Processing failed:', statusResponse.message);
          return; // Stop
        }
      } catch (error) {
        console.error('Polling error:', error);
      }

      // Continue polling
      attempts++;
      const delay = Math.min(2000 * Math.pow(2, attempts), 10000);
      setTimeout(poll, delay);
    };

    poll();
  };

  // File selection handled by FileUpload component

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Prevent double submission
    if (isSubmitting) {
      return;
    }

    // Validate required fields
    if (!uploadedFile) {
      alert('Please upload your persona document');
      return;
    }
    if (!tableNumber.trim()) {
      alert('Please enter your table number');
      return;
    }
    if (!facilitatorName.trim()) {
      alert('Please enter your facilitator name');
      return;
    }
    if (!segment) {
      alert('Please select your Temasek area origin');
      return;
    }
    if (!teamMembers.trim()) {
      alert('Please list the participating team members');
      return;
    }
    if (!highlights.trim()) {
      alert('Please share the highlights of the activity');
      return;
    }
    if (!challenges.trim()) {
      alert('Please share the challenges or surprises from the activity');
      return;
    }
    if (usefulness === null) {
      alert('Please rate how useful the session was');
      return;
    }

    setIsSubmitting(true);
    setUploadedFile(prev => prev ? { ...prev, status: 'uploading', message: 'Submission processing...' } : null);

    try {
      // Upload with exponential backoff retry
      const uploadWithRetry = async (attempt = 0): Promise<ProcessingStatus> => {
        const maxRetries = 3;
        const delay = Math.min(1000 * Math.pow(2, attempt), 5000); // 1s, 2s, 4s, max 5s

        try {
          // Upload file with workshop metadata
          const formData = new FormData();
          formData.append('file', uploadedFile.file);
          formData.append('table_number', tableNumber.trim());
          formData.append('facilitator_name', facilitatorName.trim());
          formData.append('segment', segment);
          formData.append('team_members', teamMembers.trim());
          formData.append('highlights', highlights.trim());
          formData.append('challenges', challenges.trim());
          formData.append('usefulness_rating', usefulness.toString());

          const data = await apiClient.post<ProcessingStatus>(
            `/personas/workshop/submit`,
            formData,
            { headers: { 'Content-Type': 'multipart/form-data' } }
          );
          return data;
        } catch (error) {
          const status = (error as { status?: number } | undefined)?.status;
          const shouldRetry = (status === undefined || status >= 500) && attempt < maxRetries;
          if (shouldRetry) {
            setUploadedFile(prev => prev ? {
              ...prev,
              message: `Upload failed, retrying (${attempt + 1}/${maxRetries})...`,
            } : null);
            
            await new Promise(resolve => setTimeout(resolve, delay));
            return uploadWithRetry(attempt + 1);
          }
          throw error;
        }
      };

      const result = await uploadWithRetry();
      const fileId = result.fileId;

      // Show success only if metadata is saved
      if (result.status === 'metadata_saved') {
        setSubmissionMessage(result.message || 'Submission successful! Your document has been processed.');
        setSubmitted(true);
      } else {
        throw new Error('Upload incomplete - please try again');
      }


      // Optional: Poll in background (doesn't block user, just logs to console)
      if (fileId) {
        pollStatusInBackground(fileId);
      } else {
        console.warn('No fileId returned from workshop submit response:', result);
      }

    } catch (error) {
      console.error('Upload failed:', error);
      setUploadedFile(prev => prev ? {
        ...prev,
        status: 'error',
        message: 'Upload failed. Please try again.',
      } : null);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResetForm = () => {
    setSubmitted(false);
    setSubmissionMessage(null);
    setUploadedFile(null);
    setTableNumber('');
    setFacilitatorName('');
    setSegment('');
    setTeamMembers('');
    setHighlights('');
    setChallenges('');
    setUsefulness(null);
    // File input is managed inside FileUpload component
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="border-b border-[#291B4C]/10">
        <div className="max-w-[1000px] mx-auto px-8 py-8">
          <div className="flex items-center gap-3 mb-6">
            <img 
              src="/TemasekHoldings_logo.svg" 
              alt="Temasek Holdings" 
              className="h-12"
            />
            <img
              src="/advancelogo.png"
              alt="Advance"
              className="h-6"
            />
          </div>
          <h1 className="text-3xl font-bold text-[#291B4C] tracking-tight uppercase mb-2">
            TA25 Afternoon Breakout Activity Submission
          </h1>
          <p className="text-sm text-[#291B4C]/60">
            Submit your investment persona document and activity feedback
          </p>
        </div>
      </div>

      {/* Body */}
      <div className="max-w-[1000px] mx-auto px-8 py-12">
        {submitted ? (
          <div className="border border-[#291B4C]/10 rounded-2xl p-10 bg-white text-center">
            <CheckCircle2 className="w-12 h-12 text-green-600 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-[#291B4C] mb-2 uppercase tracking-tight">Submission processed!</h2>
            <p className="text-sm text-[#291B4C]/80 mb-8">
              {submissionMessage || 'Your submission was received and is now processing.'}
            </p>
            <button
              type="button"
              onClick={handleResetForm}
              className="inline-flex items-center justify-center px-6 py-3 rounded-lg bg-[#291B4C] text-white hover:bg-[#291B4C]/90 text-sm font-semibold uppercase tracking-wide"
            >
              Submit another
            </button>
          </div>
        ) : (
        <form onSubmit={handleSubmit} className="space-y-8">
          {/* File Upload */}
          <div className="space-y-3">
            <label className="block">
              <span className="text-sm font-semibold text-[#291B4C] uppercase tracking-wide">
                1. Upload your Persona.docx <span className="text-red-600">*</span>
              </span>
              <span className="block text-xs text-[#291B4C]/60 mt-1 mb-3">
                Include your table number in file name (e.g., "Persona Table 7A.docx")
              </span>
            </label>

            <FileUpload
              value={uploadedFile}
              onSelect={(file) => setUploadedFile({ file, status: 'success', message: 'File ready to submit' })}
              onClear={() => setUploadedFile(null)}
              disabled={isSubmitting}
              acceptedExtensions={['.docx', '.doc', '.txt']}
            />
          </div>

          {/* Table Number */}
          <div className="space-y-2">
            <label className="block">
              <span className="text-sm font-semibold text-[#291B4C] uppercase tracking-wide">
                2. Table & Group Number <span className="text-red-600">*</span>
              </span>
              <span className="block text-xs text-[#291B4C]/60 mt-1 mb-3">
                e.g., 7A
              </span>
            </label>
            <input
              type="text"
              value={tableNumber}
              onChange={(e) => setTableNumber(e.target.value)}
              placeholder="Enter your table number"
              className="w-full px-4 py-3 border border-[#291B4C]/20 rounded-lg focus:outline-none focus:border-[#291B4C] text-[#291B4C]"
              required
            />
          </div>

          {/* Temasek Area Origin */}
          <div className="space-y-2">
            <label className="block">
              <span className="text-sm font-semibold text-[#291B4C] uppercase tracking-wide">
                3. Temasek Portfolio Segment <span className="text-red-600">*</span>
              </span>
              <span className="block text-xs text-[#291B4C]/60 mt-1 mb-3">
                Choose the Temasek portfolio segment that represents your table.
              </span>
            </label>
            <select
              value={segment}
              onChange={(event) => {
                const value = event.target.value;
                if (value === 'GDI' || value === 'TPC' || value === 'PFA') {
                  setSegment(value);
                } else {
                  setSegment('');
                }
              }}
              className="w-full px-4 py-3 border border-[#291B4C]/20 rounded-lg focus:outline-none focus:border-[#291B4C] text-[#291B4C] bg-white"
              required
            >
              <option value="">Select a segment</option>
              <option value="GDI">GDI — Global Direct Investments</option>
              <option value="TPC">TPC — Temasek Portfolio Companies</option>
              <option value="PFA">PFA — Partnership Funds & Asset Management</option>
            </select>
          </div>

          {/* Facilitator Name */}
          <div className="space-y-2">
            <label className="block">
              <span className="text-sm font-semibold text-[#291B4C] uppercase tracking-wide">
                4. Facilitator Name <span className="text-red-600">*</span>
              </span>
              <span className="block text-xs text-[#291B4C]/60 mt-1 mb-3">
                Enter the name of the facilitator leading your table
              </span>
            </label>
            <input
              type="text"
              value={facilitatorName}
              onChange={(e) => setFacilitatorName(e.target.value)}
              placeholder="Enter facilitator name"
              className="w-full px-4 py-3 border border-[#291B4C]/20 rounded-lg focus:outline-none focus:border-[#291B4C] text-[#291B4C]"
              required
            />
          </div>

          {/* Team Members */}
          <div className="space-y-2">
            <label className="block">
              <span className="text-sm font-semibold text-[#291B4C] uppercase tracking-wide">
                5. List of Participating Team Members <span className="text-red-600">*</span>
              </span>
              <span className="block text-xs text-[#291B4C]/60 mt-1 mb-3">
                List all team members who took part. Separate names with commas.
              </span>
            </label>
            <textarea
              value={teamMembers}
              onChange={(e) => setTeamMembers(e.target.value)}
              placeholder="Enter team member names"
              rows={3}
              className="w-full px-4 py-3 border border-[#291B4C]/20 rounded-lg focus:outline-none focus:border-[#291B4C] text-[#291B4C] resize-none"
              required
            />
          </div>

          {/* Highlights */}
          <div className="space-y-2">
            <label className="block">
              <span className="text-sm font-semibold text-[#291B4C] uppercase tracking-wide">
                6. What were the highlights of the activity? <span className="text-red-600">*</span>
              </span>
              <span className="block text-xs text-[#291B4C]/60 mt-1 mb-3">
                What did you find most engaging or valuable during the session?
              </span>
            </label>
            <textarea
              value={highlights}
              onChange={(e) => setHighlights(e.target.value)}
              placeholder="Share your thoughts..."
              rows={4}
              className="w-full px-4 py-3 border border-[#291B4C]/20 rounded-lg focus:outline-none focus:border-[#291B4C] text-[#291B4C] resize-none"
              required
            />
          </div>

          {/* Challenges */}
          <div className="space-y-2">
            <label className="block">
              <span className="text-sm font-semibold text-[#291B4C] uppercase tracking-wide">
                7. What were some challenges or surprises? <span className="text-red-600">*</span>
              </span>
              <span className="block text-xs text-[#291B4C]/60 mt-1 mb-3">
                Were there any technical issues or unexpected outcomes?
              </span>
            </label>
            <textarea
              value={challenges}
              onChange={(e) => setChallenges(e.target.value)}
              placeholder="Share your experience..."
              rows={4}
              className="w-full px-4 py-3 border border-[#291B4C]/20 rounded-lg focus:outline-none focus:border-[#291B4C] text-[#291B4C] resize-none"
              required
            />
          </div>

          {/* Usefulness Rating */}
          <div className="space-y-3">
            <label className="block">
              <span className="text-sm font-semibold text-[#291B4C] uppercase tracking-wide">
                8. How useful was this session? <span className="text-red-600">*</span>
              </span>
              <span className="block text-xs text-[#291B4C]/60 mt-1 mb-4">
                Rate the value in terms of learning and relevance
              </span>
            </label>
            <div className="flex gap-2">
              {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((value) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setUsefulness(value)}
                  className={cn(
                    'flex-1 py-3 border rounded-lg text-sm font-medium transition-colors',
                    usefulness === value
                      ? 'bg-[#291B4C] text-white border-[#291B4C]'
                      : 'bg-white text-[#291B4C] border-[#291B4C]/20 hover:border-[#291B4C]/40'
                  )}
                >
                  {value}
                </button>
              ))}
            </div>
            <div className="flex justify-between text-xs text-[#291B4C]/60">
              <span>Not useful at all</span>
              <span>Extremely useful</span>
            </div>
          </div>

          {/* Submit Button */}
          <div className="pt-6">
            <button
              type="submit"
              disabled={isSubmitting || isFormIncomplete}
              className={cn(
                'w-full py-4 rounded-lg font-semibold uppercase tracking-wide text-sm',
                isSubmitting || isFormIncomplete
                  ? 'bg-[#291B4C]/30 text-white cursor-not-allowed'
                  : 'bg-[#291B4C] text-white hover:bg-[#291B4C]/90'
              )}
            >
              {isSubmitting ? 'Submitting...' : 'Submit'}
            </button>
          </div>
        </form>
        )}
      </div>

      {/* Footer */}
      <div className="border-t border-[#291B4C]/10 mt-12">
        <div className="max-w-[1000px] mx-auto px-8 py-8">
          <p className="text-[11px] text-[#291B4C]/60 text-center">
            Tomoro will delete any personal data uploaded after the event. Only your AI persona document will be processed by OpenAI API.
          </p>
        </div>
      </div>
    </div>
  );
}
